import { toHoverColor, toPressColor, useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { HorizBox, HoverView, Pad, PadBox } from 'component/basics';
import { PopoverTip } from 'component/popup/popover-tip';
import { UtilityText } from 'component/text';
import { useTranslation } from 'component/translation';
import { useState } from 'react';
import { View } from 'react-native';
import { makeTestID, type TLabel } from 'util/stdtypes';

export type ToggleSwitchProps = {
    emoji?: string;
    text?: string;
    label?: TLabel;
    infoText?: string;
    infoLabel?: TLabel;
    popupGap?: number;
    value?: boolean;
    spread?: boolean;
    onChange?: (value: boolean) => void;
    testID?: string;
    disabled?: boolean;
};

export function ToggleSwitch({
    emoji,
    text,
    label,
    infoText,
    infoLabel,
    popupGap,
    value = false,
    spread = false,
    onChange,
    testID,
    disabled = false,
}: ToggleSwitchProps) {
    const { colors } = useTheme();
    const s = useToggleStyles();
    const tLabel = useTranslation(label);
    const [isHovered, setIsHovered] = useState(false);
    const [isPressed, setIsPressed] = useState(false);

    const toggleZoneBaseStyle = [s.toggleZone, isHovered ? s.toggleZoneHovered : null, isPressed ? s.toggleZonePressed : null];

    return (
        <PadBox vert={8}>
            <HorizBox center shrink spread={spread}>
                <HorizBox center shrink>
                    {emoji && (
                        <PadBox right={8}>
                            <UtilityText aria-hidden text={emoji} type='tiny' weight='strong' />
                        </PadBox>
                    )}
                    <UtilityText color={disabled ? colors.colorTextDisabled : undefined} label={label} text={text} />
                    {(infoLabel || infoText) && (
                        <PadBox left={4}>
                            <PopoverTip label={infoLabel} popupGap={popupGap} text={infoText} />
                        </PadBox>
                    )}
                </HorizBox>
                <Pad size={12} />
                <HoverView
                    aria-disabled={disabled}
                    ariaText={value ? `${text ?? tLabel}, checked` : `${text ?? tLabel}, unchecked`}
                    disabled={disabled}
                    onPress={() => {
                        if (disabled) return;
                        onChange?.(!value);
                    }}
                    role='button'
                    setHover={disabled ? undefined : setIsHovered}
                    setPressed={disabled ? undefined : setIsPressed}
                    shrink
                    testID={makeTestID({ testID, label, text })}
                >
                    <View style={[toggleZoneBaseStyle, value ? s.toggleZoneSelected : null, disabled ? s.toggleZoneDisabled : null]}>
                        <View style={[s.toggleBall, value ? s.toggleBallSelected : null, disabled ? s.toggleBallDisabled : null]} />
                    </View>
                </HoverView>
            </HorizBox>
        </PadBox>
    );
}

const useToggleStyles = createThemedStyles((tokens) => ({
    toggleZone: {
        width: 56,
        height: 32,
        backgroundColor: tokens.colors.colorSurfaceToggleBackground,
        borderRadius: 100,
        ...({ transition: 'background-color 0.2s ease-in-out' } as any),
    },
    toggleBall: {
        boxShadow: '2px 0px 10px rgba(0, 0, 0, 0.10)' as any, // FIXME: boxShadow not typed for native
        elevation: 5, // for Android,
        position: 'absolute',
        left: 3,
        top: 3,
        width: 26,
        height: 26,
        borderRadius: 100,
        backgroundColor: tokens.colors.colorSurfacePrimary,
        ...({ transition: 'left 0.2s ease-in-out, background-color 0.2s ease-in-out' } as any),
    },
    toggleZoneHovered: {
        backgroundColor: toHoverColor(tokens.colors.colorBorderPrimary),
    },
    toggleZonePressed: {
        backgroundColor: toPressColor(tokens.colors.colorBorderPrimary),
    },
    toggleZoneSelected: {
        backgroundColor: tokens.colors.colorTagAccentLight,
    },
    toggleBallSelected: {
        backgroundColor: tokens.colors.colorButtonAccent,
        left: 56 - 26 - 3,
    },
    toggleZoneDisabled: {
        backgroundColor: tokens.colors.colorSurfaceToggleBackground,
    },
    toggleBallDisabled: {
        backgroundColor: tokens.colors.colorButtonDisabled,
    },
}));
